import React, { useState } from 'react';
import {
  Box,
  Text,
  Heading,
  Spinner,
  Flex,
  IconButton,
  Button,
  useToast,
} from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';

const RETRIEVE_API =
  'https://n441mm4tfl.execute-api.eu-west-2.amazonaws.com/prod/retrieveapikeys';

const pageShell = {
  bg: 'brand.900' as const,
  color: 'brand.yellow' as const,
  minH: '100vh',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  px: 6,
  py: 8,
};

export default function RetrieveKey() {
  const [apiKey, setApiKey] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const toast = useToast();

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const handleReveal = async () => {
    if (!token) {
      setError('Missing token in the URL.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${RETRIEVE_API}?token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.apiKey) {
        setApiKey(data.apiKey);
        setExpiresAt(data.expiresAt);
      } else {
        setError(data.error || 'Unknown error.');
      }
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  };

  if (error) {
    return (
      <Box {...pageShell}>
        <Text color="red.400" textAlign="center" maxW="md">{error}</Text>
      </Box>
    );
  }

  if (!confirmed || loading) {
    return (
      <Box {...pageShell}>
        <Heading size="md" mb={5}>Retrieve Your API Key</Heading>
        {loading ? (
          <Flex direction="column" align="center" gap={4}>
            <Spinner color="brand.yellow" size="xl" />
            <Text>Retrieving your key…</Text>
          </Flex>
        ) : (
          <>
            <Text mb={6} maxW="md" textAlign="center">
              For your security, your API key will only be shown once. When you are ready, click the button below to reveal your key.
            </Text>
            <Button
              colorScheme="yellow"
              size="lg"
              onClick={() => {
                setConfirmed(true);
                handleReveal();
              }}
            >
              Reveal API Key
            </Button>
          </>
        )}
      </Box>
    );
  }

  if (apiKey) {
    return (
      <Box {...pageShell} justifyContent="flex-start" pt={{ base: 10, md: 28 }}>
        <Heading size="md" mb={4}>Your API Key</Heading>
        <Flex align="center" gap={2} mb={4}>
          <Box
            fontFamily="monospace"
            fontSize="xl"
            bg="brand.800"
            px={4}
            py={3}
            borderRadius="md"
            border="1px solid"
            borderColor="brand.yellow"
          >
            {apiKey}
          </Box>
          <IconButton
            aria-label="Copy API Key"
            icon={<CopyIcon boxSize={6} />}
            colorScheme="yellow"
            variant="outline"
            size="lg"
            onClick={() => {
              navigator.clipboard.writeText(apiKey);
              toast({
                title: 'Copied!',
                description: 'API Key copied to clipboard.',
                status: 'success',
                duration: 1500,
                isClosable: true,
              });
            }}
          />
        </Flex>
        {expiresAt && <Text mb={3}>Expires: {expiresAt}</Text>}
        <Text color="red.400">
          <b>Note:</b> You will not be able to retrieve this key again. Copy it now.
        </Text>
      </Box>
    );
  }

  return null;
}
